import { useState } from 'react';

// Mapping type -> nom de modale
const MODAL_BY_TYPE = {
  identity: 'ad_file_accept_identity',
  selfie: 'ad_file_accept_simple',
  visa: 'ad_file_accept_visa',
  card_vitality: 'ad_file_accept_card_vitality',
  proof_of_address: 'ad_file_accept_simple',
  accommodation_of_address: 'ad_file_accept_simple',
  hostIdentity: 'ad_file_accept_simple',
};

// Mapping type -> clé dans tempWorker pour le statut
const VALIDATION_KEY_BY_TYPE = {
  identity: 'identity',
  selfie: 'selfie',
  visa: 'visa',
  card_vitality: 'card_vitality',
  proof_of_address: 'proof_of_address',
  accommodation_of_address: 'accommodation_of_address',
  hostIdentity: 'hostIdentity',
};

/**
 * Badge de statut de validation
 */
const StatusBadge = ({ status }) => {
  if (status === '2') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
        Validé
      </span>
    );
  }
  if (status === '1') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700">
        À vérifier
      </span>
    );
  }
  if (status === '3') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
        Refusé
      </span>
    );
  }
  return null;
};

/**
 * Composant de prévisualisation d'image/PDF
 */
const DocumentPreview = ({ doc, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const isImage = doc.fileType?.startsWith('image/') || 
    /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.uri);
  const isPdf = doc.fileType?.includes('pdf') || /\.pdf$/i.test(doc.uri) || /\.pdf$/i.test(doc.fileName);

  return (
    <div
      className="relative cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-all duration-200 w-full aspect-square bg-gray-50"
      onClick={onClick}
    >
      {isImage && !imageError ? (
        <img
          src={doc.uri}
          alt={doc.fileName}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : isPdf && !pdfError ? (
        <div className="w-full h-full relative">
          <iframe
            src={`${doc.uri}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title={doc.fileName || 'PDF'}
            className="w-full h-full border-0 pointer-events-none"
            onError={() => setPdfError(true)}
          />
          {/* Overlay pour capter le clic */}
          <div className="absolute inset-0" />
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2 bg-gray-100">
          <svg className="w-8 h-8 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-[8px] text-gray-500 text-center line-clamp-2 break-words">
            {doc.fileName || 'Document'}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Badge given by the caller, for documents whose states are not the identity ones.
 */
const CustomBadge = ({ label, className = 'bg-gray-100 text-gray-700' }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${className}`}>
    {label}
  </span>
);

/**
 * Groupe de documents d'un type
 */
const DocumentGroup = ({ type, label, docs, status, badge, onSelect, onOpenModal }) => {
  // A group carrying its own onSelect (a licence entry) is not opened through a modal name.
  const open = () => (onSelect ? onSelect() : onOpenModal(type));
  return (
    <div 
      className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
      onClick={open}
    >
      <div className="flex flex-col items-center gap-2">
        {/* Photo */}
        {docs.length > 0 && (
          <DocumentPreview
            doc={docs[0]}
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
          />
        )}
        {/* Texte + Badge */}
        <div className="w-full flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-gray-600 flex-1 text-left truncate">
            {label}
          </span>
          {badge ? <CustomBadge {...badge} /> : <StatusBadge status={status} />}
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar affichant tous les documents de l'utilisateur
 * @param {object} props
 * @param {Array} props.allDocuments - Liste de tous les documents groupés par type
 * @param {boolean} props.isLoading - État de chargement
 * @param {string} props.currentType - Type de document actuellement en cours de validation
 * @param {function} props.onOpenDocumentModal - Callback pour ouvrir la modale d'un document (reçoit le type)
 * @param {object} props.tempWorker - Données du tempWorker pour les statuts de validation
 * @param {string} props.title - Heading of the sidebar (default: "Autres documents")
 *
 * A group may also carry `id` (key when several groups share a type), `badge`
 * ({ label, className }) in place of the identity status, and `onSelect`, called
 * on click instead of opening the modal of its type.
 */
const DocumentSidebar = ({ allDocuments, isLoading, currentType, onOpenDocumentModal, tempWorker, title = 'Autres documents' }) => {
  const handleOpenModal = (type) => {
    if (onOpenDocumentModal) {
      const modalName = MODAL_BY_TYPE[type];
      onOpenDocumentModal(type, modalName);
    }
  };

  // Récupérer le statut de validation pour un type
  const getValidationStatus = (type) => {
    const key = VALIDATION_KEY_BY_TYPE[type];
    return tempWorker?.[key]?.validated;
  };

  // Ne garder que les documents différents du type actuel (déjà filtré par le hook)
  const otherDocuments = allDocuments;

  if (isLoading && otherDocuments.length === 0) {
    return (
      <div className="w-56 flex-shrink-0 h-full flex flex-col">
        <div className="bg-gray-50 rounded-lg h-full p-2 border border-gray-100 flex flex-col">
          <div className="h-4 skeleton-bar w-24 mb-3 flex-shrink-0"></div>
          <div className="flex-1 space-y-2">
            <div className="h-20 skeleton-bar"></div>
            <div className="h-20 skeleton-bar"></div>
          </div>
        </div>
      </div>
    );
  }

  if (otherDocuments.length === 0) {
    return null;
  }

  return (
    <div className="w-56 flex-shrink-0 h-full flex flex-col">
      <div className="bg-gray-50 rounded-lg h-full p-2 border border-gray-100 flex flex-col">
        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex-shrink-0">
          {title}
        </h4>
        
        <div className="flex-1 space-y-2 overflow-y-auto pr-1">
          {otherDocuments.map((docGroup) => (
            <DocumentGroup
              key={docGroup.id || docGroup.type}
              {...docGroup}
              status={getValidationStatus(docGroup.type)}
              onOpenModal={handleOpenModal}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DocumentSidebar;
